/**
 * EmailVerifyBanner — slim sticky strip when the logged-in user hasn't
 * verified their email yet. Hides automatically once verified or while the
 * verification flow is in progress.
 *
 * Methodology hold #5: calm voice. We tell the user what's happening, give
 * them one button, and step out of the way.
 */
import { useState, useEffect } from 'react';
import { Mail, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

export default function EmailVerifyBanner() {
  const { user, refreshUser } = useAuth();
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  // Re-check verification state whenever user changes
  useEffect(() => { setMsg(''); }, [user?.email]);

  if (!user || user.email_verified || hidden) return null;
  // Admin accounts skip the banner — seeded admin doesn't need verification
  if (user.role === 'admin') return null;

  const onResend = async () => {
    setBusy(true);
    setMsg('');
    try {
      const r = await api.post('/auth/resend-verification');
      if (r.data?.already_verified) {
        if (refreshUser) await refreshUser();
        setMsg('Already verified.');
      } else if (r.data?.verify_link_for_dev) {
        setMsg('Dev: ' + r.data.verify_link_for_dev);
      } else {
        setMsg('Sent. Check your inbox.');
      }
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Could not send. Try again later.');
    }
    setBusy(false);
  };

  return (
    <div
      className="pointer-events-auto flex-shrink-0 px-3 md:px-5 py-1.5 flex items-center gap-3 border-b border-[#D4A84A]/20"
      style={{ background: 'rgba(212,168,74,0.06)' }}
      data-testid="email-verify-banner"
    >
      <Mail size={13} className="text-[#D4A84A] flex-shrink-0" />
      <span className="font-mono text-[11px] md:text-xs text-white/85 flex-1 truncate">
        Confirm your email <strong className="text-[#D4A84A] font-mono">{user.email}</strong> to activate your account.
        {msg && <span className="ml-2 text-[#2F8A92]">{msg.length > 80 ? msg.slice(0, 80) + '…' : msg}</span>}
      </span>
      <button
        onClick={onResend}
        disabled={busy}
        data-testid="email-verify-resend-btn"
        className="font-hud text-[10px] md:text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-3 py-1 hover:bg-[#D4A84A]/85 disabled:opacity-50"
      >
        {busy ? 'SENDING…' : 'RESEND'}
      </button>
      <button
        onClick={() => setHidden(true)}
        data-testid="email-verify-dismiss"
        className="text-white/55 hover:text-white"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
